import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaCalculator, 
  FaCloud, 
  FaNetworkWired, 
  FaArrowRight, 
  FaTools, 
  FaLock, 
  FaCheckCircle,
  FaLightbulb,
  FaLeaf,
  FaShieldAlt,
  FaTerminal
} from 'react-icons/fa';

const toolsList = [
  {
    id: 'greenops-carbon-estimator',
    title: 'Cloud Carbon & GreenOps Estimator',
    category: 'Sustainability & GreenOps',
    version: 'v1.0 Pro',
    status: 'Active',
    icon: <FaLeaf size={28} color="#2ecc71" />,
    description: 'Enterprise sustainability and cloud decarbonization suite quantifying Scope 2 electricity grid emissions and Scope 3 hardware embodied carbon across AWS regions with automated GHG Protocol ESG audit reporting.',
    features: [
      'Multi-Region Carbon Intensity Audit (Nordic Hydro 24g vs Coal 708g/kWh)',
      'x86 vs AWS Graviton ARM64 Energy Efficiency & Server TDP Modeling',
      'Scope 3 Embodied Carbon & Circular Hardware Lifespan Amortization',
      'Real-World Equivalences (Gasoline car miles, trees sequestered, flights)',
      'GreenOps Net-Zero Levers & CSRD / GHG Protocol ESG Report Generator'
    ],
    path: '/tools/greenops-carbon-estimator',
    buttonText: 'Launch GreenOps',
    featured: true
  },
  {
    id: 'cloud-cost-estimator',
    title: 'AWS & Multi-Cloud Infrastructure Estimator',
    category: 'Cloud Architecture',
    version: 'v1.0 Pro',
    status: 'Active',
    icon: <FaCloud size={28} color="#00d2d3" />,
    description: 'Interactive workload sizing, monthly burst modeling, and reserved instance cost optimization for EC2, S3, RDS, ALB, and VPC NAT gateways with instant Terraform IaC generation.',
    features: [
      'Multi-Service Architecture Sizer & Bill of Materials (EC2, S3, RDS, NAT, ALB)',
      'FinOps 1-Yr & 3-Yr Savings Plans / RI discount model (up to 62% savings)',
      'Traffic Burst & Black Friday elasticity simulator (1x to 10x surge slider)',
      'Multi-Tier High-Availability visual topology diagram',
      'Automated Terraform HCL Infrastructure as Code generator & download'
    ],
    path: '/tools/cloud-cost-estimator',
    buttonText: 'Launch Estimator',
    featured: true
  },
  {
    id: 'network-subnet-calculator',
    title: 'Cisco CIDR & Subnetting Engineering Lab',
    category: 'Network Engineering',
    version: 'v1.0 Pro',
    status: 'Active',
    icon: <FaNetworkWired size={28} color="#00d2d3" />,
    description: 'Precision bitwise IPv4 subnet calculator, hierarchical Variable Length Subnet Masking (VLSM) dynamic allocator, automated Cisco IOS CLI script generator (OSPF, ACLs, DHCP), and interactive CCNA certification drill practice.',
    features: [
      '32-bit binary octet bitwise breakdown & coloring',
      'Cisco Inverted Wildcard Mask computation & Hexadecimal masks',
      'Hierarchical VLSM allocator with major network capacity tracking',
      'Cisco IOS CLI script generator (Interfaces, OSPF, ACLs, DHCP)',
      'CCNA Subnetting Mastery drill & quiz simulator',
      'Interactive CIDR quick lookup reference matrix (/8 to /32)'
    ],
    path: '/tools/cisco-subnet-lab',
    buttonText: 'Launch Subnet Lab',
    featured: true
  },
  {
    id: 'bill-tracker',
    title: 'Bill Tracker & Cash Flow Engine',
    category: 'Finance & Operations',
    version: 'v2.0 Pro',
    status: 'Active',
    icon: <FaCalculator size={28} color="#45aaf2" />,
    description: 'A comprehensive multi-sheet financial engine designed for dynamic Monday cash-flow streaming, invoice urgency & extension tracking, 3-week Tuesday installment planning, debt capacity ratios, and a 30-day credit card interest shield optimizer.',
    features: [
      '4-Week Monday Income Streaming Ledger',
      'Color-coded Invoice Due Date Engine (Overdue Red, On-Track Green, Extended Yellow)',
      'Invoice Urgency locks (Critical, Average, Less Important)',
      '3-Week Tuesday Installment Auto-Allocation Modal',
      'Credit Card Interest Shield & 30-Day Paydown Matrix',
      'Isolated Multi-User Local Storage Security'
    ],
    path: '/bill-tracker',
    buttonText: 'Open Bill Tracker',
    featured: true
  },
  {
    id: 'linux-devops-suite',
    title: 'Linux & DevOps Automation Suite',
    category: 'Linux & DevOps',
    version: 'v1.0 Pro',
    status: 'Active',
    icon: <FaTerminal size={28} color="#e67e22" />,
    description: 'RHEL/Fedora enterprise system engineering workbench featuring visual Systemd Service Unit generation, Systemd Timer & dual-mode Cron translation, CIS Security Benchmark daemon audit scoring, and idempotent Bash deployment script generation.',
    features: [
      'Visual Systemd Unit Architect (Restart policies, ExecStart, Cgroup resource controls)',
      'Dual-Mode Cron & Systemd Timer Scheduler with next 5 executions projection',
      'CIS Security Benchmark Daemon Hardening Auditor (NoNewPrivileges, ProtectSystem)',
      'Idempotent Bash deployment script synthesizer (systemctl daemon-reload & journalctl)',
      'Pre-configured Production Presets (Node.js API, Python FastAPI, Postgres Backup, Go Daemon)'
    ],
    path: '/tools/linux-devops-suite',
    buttonText: 'Launch DevOps Suite',
    featured: true
  },
  {
    id: 'siem-threat-hunter',
    title: 'SIEM Log Parser & MITRE ATT&CK Threat Hunter',
    category: 'Cybersecurity & Threat Ops',
    version: 'v1.0 Preview',
    status: 'In Development',
    icon: <FaShieldAlt size={28} color="#ff7675" />,
    description: 'Security operations simulator parsing auth, web, and firewall syslogs, detecting attack vectors (SSH brute-force, SQL injection, XSS), mapping directly to MITRE ATT&CK techniques, and generating Snort/Suricata rules.',
    features: [
      'Raw syslog & auth.log heuristic pattern matching',
      'MITRE ATT&CK Enterprise Matrix technique mapping (T1110, T1190)',
      'Live threat severity score & anomalous IP geographic extraction',
      'Automated Snort / Suricata detection rules & iptables block scripts'
    ],
    path: null,
    buttonText: 'In Development',
    featured: false
  }
];

const ToolsPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = [
    'All',
    'Linux & DevOps',
    'Sustainability & GreenOps',
    'Cloud Architecture',
    'Network Engineering',
    'Cybersecurity & Threat Ops',
    'Finance & Operations'
  ];

  const filteredTools = activeCategory === 'All' 
    ? toolsList 
    : toolsList.filter(t => t.category === activeCategory);

  return (
    <div style={{
      minHeight: '90vh',
      backgroundColor: '#040d1a',
      color: '#e2e8f0',
      padding: '48px 20px 80px 20px',
      background: 'radial-gradient(ellipse at top, #0b1e36 0%, #040d1a 70%)'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* Breadcrumb / Top label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', color: '#45aaf2', fontSize: '0.9rem', fontWeight: 600 }}>
          <FaTools />
          <Link to="/" style={{ color: '#94a3b8', textDecoration: 'none' }}>Home</Link>
          <span style={{ color: '#64748b' }}>/</span>
          <span>Engineering &amp; Productivity Tools</span>
        </div>

        {/* Header Hero */}
        <div style={{
          backgroundColor: 'rgba(11, 28, 52, 0.7)',
          border: '1px solid rgba(69, 170, 242, 0.25)',
          borderRadius: '16px',
          padding: '36px 32px',
          backdropFilter: 'blur(10px)',
          marginBottom: '40px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
        }}>
          <div style={{ display: 'inline-block', background: 'rgba(69, 170, 242, 0.15)', border: '1px solid rgba(69, 170, 242, 0.3)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', color: '#45aaf2', fontWeight: 600, marginBottom: '12px' }}>
            🛠️ Adedeji Tooling Suite
          </div>
          <h1 style={{ fontSize: '2.4rem', color: '#ffffff', margin: '0 0 14px 0', fontWeight: 700, letterSpacing: '-0.5px' }}>
            Interactive Tools &amp; Utilities
          </h1>
          <p style={{ fontSize: '1.05rem', color: '#94a3b8', maxWidth: '780px', lineHeight: '1.6', margin: 0 }}>
            A growing portfolio of custom tools, operational models, and workflow utilities engineered by Adedeji Babalola. These applications are built to streamline cash flow forecasting, cloud infrastructure planning, and technical analysis.
          </p>

          {/* Category Filter Pills */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '24px', flexWrap: 'wrap' }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: '8px 18px',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: activeCategory === cat ? '1px solid #45aaf2' : '1px solid rgba(255, 255, 255, 0.12)',
                  backgroundColor: activeCategory === cat ? '#45aaf2' : 'rgba(255, 255, 255, 0.04)',
                  color: activeCategory === cat ? '#040d1a' : '#cbd5e1',
                  transition: 'all 0.2s ease'
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Tools Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: '28px'
        }}>
          {filteredTools.map(tool => (
            <div
              key={tool.id}
              style={{
                backgroundColor: 'rgba(8, 20, 39, 0.85)',
                border: tool.featured ? '1.5px solid rgba(69, 170, 242, 0.45)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '30px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                boxShadow: tool.featured 
                  ? '0 10px 30px rgba(69, 170, 242, 0.12), inset 0 1px 0 rgba(69, 170, 242, 0.3)' 
                  : '0 6px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {tool.featured && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  background: 'linear-gradient(135deg, #45aaf2, #2d98da)',
                  color: '#040d1a',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  padding: '4px 16px',
                  borderBottomLeftRadius: '10px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Featured Tool
                </div>
              )}

              <div>
                {/* Header info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
                  <div style={{
                    width: '54px',
                    height: '54px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(69, 170, 242, 0.1)',
                    border: '1px solid rgba(69, 170, 242, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {tool.icon}
                  </div>
                  <div>
                    <span style={{ fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                      {tool.category}
                    </span>
                    <h2 style={{ fontSize: '1.28rem', color: '#ffffff', margin: '2px 0 0 0', fontWeight: 700 }}>
                      {tool.title}
                    </h2>
                  </div>
                </div>

                {/* Status & Version */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '6px',
                    backgroundColor: tool.status === 'Active' ? 'rgba(46, 204, 113, 0.15)' : 'rgba(248, 210, 16, 0.15)',
                    color: tool.status === 'Active' ? '#2ecc71' : '#f8d210',
                    border: `1px solid ${tool.status === 'Active' ? 'rgba(46, 204, 113, 0.3)' : 'rgba(248, 210, 16, 0.3)'}`
                  }}>
                    ● {tool.status}
                  </span>
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#94a3b8',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    padding: '3px 8px',
                    borderRadius: '6px'
                  }}>
                    {tool.version}
                  </span>
                </div>

                {/* Description */}
                <p style={{ fontSize: '0.92rem', color: '#cbd5e1', lineHeight: '1.6', marginBottom: '20px' }}>
                  {tool.description}
                </p>

                {/* Key Capabilities */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>
                    Capabilities &amp; Features:
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {tool.features.map((feat, idx) => (
                      <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.86rem', color: '#94a3b8' }}>
                        <FaCheckCircle style={{ color: '#45aaf2', marginTop: '3px', flexShrink: 0 }} size={12} />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Action Button */}
              <div style={{ paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                {tool.path ? (
                  <Link
                    to={tool.path}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '12px 20px',
                      borderRadius: '10px',
                      backgroundColor: '#45aaf2',
                      color: '#040d1a',
                      fontWeight: 700,
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      boxShadow: '0 4px 14px rgba(69, 170, 242, 0.3)',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                  >
                    <span>{tool.buttonText}</span>
                    <FaArrowRight size={14} />
                  </Link>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '12px 20px',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#64748b',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      cursor: 'not-allowed',
                      boxSizing: 'border-box'
                    }}
                  >
                    <FaLock size={12} />
                    <span>{tool.buttonText}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Information box on creating / adding future tools */}
        <div style={{
          marginTop: '48px',
          backgroundColor: 'rgba(15, 39, 68, 0.5)',
          border: '1px solid rgba(69, 170, 242, 0.2)',
          borderRadius: '14px',
          padding: '24px 28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', maxWidth: '720px' }}>
            <div style={{
              width: '46px',
              height: '46px',
              borderRadius: '10px',
              backgroundColor: 'rgba(69, 170, 242, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#45aaf2',
              flexShrink: 0
            }}>
              <FaLightbulb size={22} />
            </div>
            <div>
              <h4 style={{ margin: '0 0 4px 0', color: '#ffffff', fontSize: '1.05rem' }}>
                Extensible Engineering Workspace
              </h4>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem', lineHeight: '1.5' }}>
                All tools operate in isolated client-side environments with instant calculation, data privacy, and zero external tracking. Future simulation modules and calculators will automatically plug in here.
              </p>
            </div>
          </div>
          <Link
            to="/contact"
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid rgba(69, 170, 242, 0.4)',
              color: '#45aaf2',
              backgroundColor: 'rgba(69, 170, 242, 0.1)',
              textDecoration: 'none',
              fontSize: '0.88rem',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            Request a Custom Tool →
          </Link>
        </div>

      </div>
    </div>
  );
};

export default ToolsPage;
